import type { AtlasViewerMode } from '@atlas/api-contract';
import { MessageSquarePlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface CommentModeToggleProps {
  readonly mode: AtlasViewerMode;
  readonly onChange: (mode: AtlasViewerMode) => void;
  /** The agent is not reachable, so a click could not become a pin. */
  readonly disabled?: boolean;
}

/**
 * The one control that changes what a click *means* (spec R3.3).
 *
 * In `browse` a click reaches the prototype and does whatever the prototype
 * does; in `comment` the agent intercepts it and turns it into a pin. Because
 * the same gesture has two outcomes, the toggle has to be unmistakably on or
 * off at a glance — so the active state is a filled button rather than a subtle
 * outline, and `aria-pressed` carries the same fact to a screen reader.
 *
 * It is disabled, not hidden, when the agent is unreachable: the reviewer can
 * still browse, and a control that vanished would read as a missing feature
 * rather than a temporary one.
 */
export const CommentModeToggle = ({
  mode,
  onChange,
  disabled = false,
}: CommentModeToggleProps) => {
  const active = mode === 'comment';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant={active ? 'default' : 'outline'}
          aria-pressed={active}
          disabled={disabled}
          className={
            active
              ? 'h-8 gap-1.5 px-2.5 text-xs font-normal'
              : 'h-8 gap-1.5 border-viewer-chrome-border bg-transparent px-2.5 text-xs font-normal text-viewer-chrome-foreground'
          }
          onClick={() => {
            onChange(active ? 'browse' : 'comment');
          }}
        >
          <MessageSquarePlus aria-hidden="true" className="size-3.5" />
          Comment
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {disabled
          ? 'Unavailable until the prototype responds'
          : active
            ? 'Clicks create pins. Turn off to use the prototype.'
            : 'Turn on to place a comment pin with a click.'}
      </TooltipContent>
    </Tooltip>
  );
};
